import { Link } from "react-router-dom";
import {
  Factory,
  Fingerprint,
  GraduationCap,
  HeartPulse,
  Printer,
  Radio,
  Scale,
} from "lucide-react";
import { PageShell, PageHero } from "../components/PageShell";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { CtaBanner } from "../components/CtaBanner";
import { solutions, industries } from "../data/content";

const solutionIcons = [GraduationCap, Factory, Printer, HeartPulse, Fingerprint, Scale, Radio];

const spanMap = [
  "md:col-span-7",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-7",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
];

export default function Solutions() {
  return (
    <PageShell>
      <div data-testid="solutions-page">
        <PageHero
          overline="Solutions"
          title="Technology We've Built."
          description="We have experience delivering software solutions across different industries, business processes, and technology environments."
        />

        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-4 md:grid-cols-12">
              {solutions.map((solution, i) => {
                const Icon = solutionIcons[i];
                return (
                  <Reveal key={solution.title} delay={0.05 * (i % 3)} className={spanMap[i]}>
                    <Link
                      to={`/solutions/${solution.slug}`}
                      className="card-gradient group flex h-full flex-col rounded-xl border border-white/[0.08] p-8 transition-colors duration-300 hover:border-cyan-400/40"
                      data-testid={`solution-card-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="grid h-11 w-11 place-items-center rounded-lg border border-white/10 bg-obsidian transition-colors duration-300 group-hover:border-cyan-400/40">
                          <Icon className="h-5 w-5 text-cyan-400" strokeWidth={1.75} />
                        </span>
                        <span className="font-mono text-[11px] tracking-[0.2em] text-slate-600">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <h2 className="mt-6 font-display text-xl sm:text-2xl font-semibold tracking-tight text-white">
                        {solution.title}
                      </h2>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">
                        {solution.desc}
                      </p>
                      <div className="mt-6 flex flex-wrap gap-2 border-t border-white/[0.07] pt-5">
                        {solution.tech.map((t) => (
                          <span key={t} className="chip">
                            {t}
                          </span>
                        ))}
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={0.15}>
              <p className="mt-10 font-mono text-xs tracking-wide text-slate-500">
                * The solutions above are derived from professional project experience.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="border-t border-white/[0.07] bg-obsidian/30 py-20 lg:py-28" data-testid="industries-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading overline="Industries" title="Technology Across Industries." />
            <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-3">
              {industries.map((industry, i) => (
                <Reveal key={industry.title} delay={0.05 * i} className="h-full">
                  <div className="h-full bg-void p-8 transition-colors duration-300 hover:bg-obsidian" data-testid={`industry-card-${i}`}>
                    <span className="font-mono text-xs tracking-[0.2em] text-cyan-400/80">
                      0{i + 1}
                    </span>
                    <h3 className="mt-4 font-display text-lg font-semibold text-white">
                      {industry.title}
                    </h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{industry.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <CtaBanner
          title="Have a Similar Challenge?"
          text="Every solution above started as a business problem. Tell us yours."
        />
      </div>
    </PageShell>
  );
}
